/**
 * A type-safe utility that creates a proxy object to capture property access
 * for translation keys and convert it to a string literal type.
 *
 * @example
 * // Create a type-safe translation field accessor
 * const i18n = createTranslationField<I18nScenarioEditorNs>();
 *
 * // Use it with translate function - this is type-safe and will error if 'sharedScenarioSettings' is not a property of I18nScenarioEditorNs
 * translate(i18n.sharedScenarioSettingsDialog.sharedScenarioSettings)
 *
 * // For nested properties, you can chain the accessor
 * translate(i18n.sharedScenarioSettingsDialog.selectAnOrganization)
 */
export function createTranslationField<T>(): T {
  const buildProxy = (path: string[]): unknown =>
    new Proxy(
      // Use a function as target so it has proper primitive conversion
      Object.assign(() => {}, {
        toString: () => path.join('.'),
        valueOf: () => path.join('.')
      }),
      {
        get: (_target, prop) => {
          if (typeof prop === 'symbol') {
            return undefined;
          }
          if (prop === 'toString' || prop === 'valueOf') {
            return () => path.join('.');
          }
          return buildProxy([...path, String(prop)]);
        }
      }
    );

  // Create a proxy object that captures property access with arbitrary depth
  return buildProxy([]) as T;
}
